import { useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { 
  LayoutDashboard, 
  Clock, 
  FolderKanban, 
  CreditCard, 
  Calculator, 
  FileBarChart,
  X,
  ChevronRight
} from "lucide-react"
import { Button } from "@/components/ui/button"
import { useCompleteTour } from "@/hooks/useOnboarding"
import { useNavigate } from "react-router-dom"

interface GuidedTourProps {
  onComplete: () => void
}

const tourSteps = [
  {
    id: 'dashboard',
    route: '/dashboard',
    icon: LayoutDashboard,
    title: 'Dashboard',
    description: 'Sua central de inteligência financeira com insights em tempo real.',
    color: 'from-blue-500/20 to-blue-600/10',
  },
  {
    id: 'timer',
    route: '/timer',
    icon: Clock,
    title: 'Timer',
    description: 'Registre suas horas e acompanhe o tempo dedicado a cada projeto.',
    color: 'from-emerald-500/20 to-emerald-600/10',
  },
  {
    id: 'projects',
    route: '/projects',
    icon: FolderKanban,
    title: 'Projetos',
    description: 'Gerencie seus projetos em um Kanban visual e intuitivo.',
    color: 'from-violet-500/20 to-violet-600/10',
  },
  {
    id: 'payments',
    route: '/payments',
    icon: CreditCard,
    title: 'Pagamentos',
    description: 'Controle recebimentos, parcelas e previsão de receita.',
    color: 'from-amber-500/20 to-amber-600/10',
  },
  {
    id: 'calculator',
    route: '/calculator',
    icon: Calculator,
    title: 'Calculadora',
    description: 'Descubra seu valor/hora ideal baseado nas suas metas.',
    color: 'from-rose-500/20 to-rose-600/10',
  },
  {
    id: 'reports',
    route: '/reports',
    icon: FileBarChart,
    title: 'Relatórios',
    description: 'Analise sua performance com relatórios profissionais.',
    color: 'from-cyan-500/20 to-cyan-600/10',
  },
]

export function GuidedTour({ onComplete }: GuidedTourProps) {
  const [currentStep, setCurrentStep] = useState(0)
  const completeTour = useCompleteTour()
  const navigate = useNavigate()

  const handleNext = () => {
    if (currentStep < tourSteps.length - 1) {
      const nextStep = tourSteps[currentStep + 1]
      navigate(nextStep.route)
      setCurrentStep(currentStep + 1)
    } else {
      handleComplete()
    }
  }

  const handleSkip = async () => {
    await completeTour.mutateAsync()
    onComplete()
  }

  const handleComplete = async () => {
    await completeTour.mutateAsync()
    navigate('/dashboard')
    onComplete()
  }

  const currentTourStep = tourSteps[currentStep]
  const Icon = currentTourStep.icon

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 20 }}
        className="fixed bottom-0 sm:bottom-6 left-0 sm:left-1/2 sm:-translate-x-1/2 z-50 w-full sm:max-w-md px-0 sm:px-4"
      >
        <div className={`rounded-t-2xl sm:rounded-2xl border-t sm:border shadow-2xl bg-gradient-to-r ${currentTourStep.color} backdrop-blur-xl bg-card/95 overflow-hidden`}>
          {/* Mobile drag indicator */}
          <div className="sm:hidden flex justify-center pt-2">
            <div className="w-10 h-1 bg-muted-foreground/30 rounded-full" />
          </div>
          
          {/* Progress */}
          <div className="flex gap-1 px-4 pt-3 sm:pt-4">
            {tourSteps.map((_, idx) => (
              <div
                key={idx}
                className={`h-1 flex-1 rounded-full transition-colors ${
                  idx <= currentStep ? 'bg-primary' : 'bg-muted-foreground/20'
                }`}
              />
            ))}
          </div>

          <div className="p-4">
            <div className="flex items-start gap-3 sm:gap-4">
              <div className="p-2.5 sm:p-3 rounded-xl bg-background/80 shadow-sm shrink-0">
                <Icon className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="font-semibold text-base sm:text-lg">{currentTourStep.title}</h3>
                  <span className="text-xs text-muted-foreground shrink-0">
                    {currentStep + 1}/{tourSteps.length}
                  </span>
                </div>
                <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                  {currentTourStep.description}
                </p>
              </div>
            </div>

            <div className="flex gap-2 mt-4">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={handleSkip}
                className="text-muted-foreground text-xs sm:text-sm"
              >
                <X className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1" />
                Pular
              </Button>
              
              <Button 
                onClick={handleNext}
                size="sm"
                className="ml-auto text-xs sm:text-sm"
              >
                {currentStep === tourSteps.length - 1 ? 'Concluir' : 'Próximo'}
                <ChevronRight className="h-3.5 w-3.5 sm:h-4 sm:w-4 ml-1" />
              </Button>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  )
}
